import React, { useState, useEffect, useCallback } from 'react';

interface VolumeInfo {
  mountPoint: string;
  type: string;
  server: string;
  description: string;
  services: string[];
  mounted: boolean;
  freeGB: number | null;
  totalGB: number | null;
  usedPct: number | null;
  checkedAt: string;
}

interface LogEntry {
  timestamp: string;
  level: 'INFO' | 'OK' | 'WARN' | 'ERROR';
  message: string;
}

const REFRESH_INTERVAL = 30000;

const VolumeMonitor: React.FC = () => {
  const [volumes, setVolumes] = useState<VolumeInfo[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showLogs, setShowLogs] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const showNotif = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const fetchStatus = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const res = await fetch('/api/agent/volumes/status', { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        setVolumes(data.volumes || []);
        setLastRefresh(new Date());
      }
    } catch {
      // silently ignore
    } finally {
      if (!silent) setRefreshing(false);
      setLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await fetch('/api/agent/volumes/logs?lines=80', { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
      }
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(() => fetchStatus(true), REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  useEffect(() => {
    if (showLogs) fetchLogs();
  }, [showLogs, fetchLogs]);

  const handleRemount = async (mountPoint: string) => {
    setActionLoading(`remount-${mountPoint}`);
    try {
      const res = await fetch('/api/agent/volumes/remount', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mountPoint }),
      });
      const data = await res.json();
      if (data.success) {
        showNotif('success', `Remontage lancé pour ${mountPoint}`);
        setTimeout(() => fetchStatus(), 3000);
      } else {
        showNotif('error', data.message || 'Échec du remontage');
      }
    } catch {
      showNotif('error', 'Erreur de connexion à l\'agent');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemountAll = async () => {
    setActionLoading('remount-all');
    try {
      const res = await fetch('/api/agent/volumes/remount', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mountPoint: '*' }),
      });
      const data = await res.json();
      if (data.success) {
        showNotif('success', data.message || 'Remontage de tous les volumes lancé');
        // Le remontage tourne en arrière-plan côté agent et peut prendre
        // plusieurs minutes après une coupure réseau (jusqu'à ~35s par
        // tentative, x3 tentatives, x6 volumes dans le pire cas). On
        // rafraîchit régulièrement pendant quelques minutes au lieu d'un
        // simple coup d'œil 5s après, pour voir la progression réelle.
        let checks = 0;
        const poll = setInterval(() => {
          checks++;
          fetchStatus(true);
          if (checks >= 24) clearInterval(poll); // ~4 min (24 x 10s)
        }, 10000);
      } else {
        showNotif('error', data.message || 'Échec du remontage');
      }
    } catch {
      showNotif('error', 'Erreur de connexion à l\'agent');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestartServices = async (mountPoint: string) => {
    setActionLoading(`restart-${mountPoint}`);
    try {
      const res = await fetch('/api/agent/volumes/restart-services', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mountPoint }),
      });
      const data = await res.json();
      if (data.success) {
        showNotif('success', `Services redémarrés: ${data.restarted?.map((r: any) => r.service).join(', ')}`);
      } else {
        showNotif('error', data.message || 'Échec du redémarrage');
      }
    } catch {
      showNotif('error', 'Erreur de connexion à l\'agent');
    } finally {
      setActionLoading(null);
    }
  };

  const mountedCount = volumes.filter(v => v.mounted).length;
  const disconnectedCount = volumes.length - mountedCount;

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'OK': return '#4ade80';
      case 'WARN': return '#fbbf24';
      case 'ERROR': return '#f87171';
      default: return '#94a3b8';
    }
  };

  const getLevelBg = (level: string) => {
    switch (level) {
      case 'OK': return 'rgba(74, 222, 128, 0.1)';
      case 'WARN': return 'rgba(251, 191, 36, 0.1)';
      case 'ERROR': return 'rgba(248, 113, 113, 0.1)';
      default: return 'transparent';
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <div style={{ textAlign: 'center', color: '#94a3b8' }}>
          <div style={{
            width: '32px', height: '32px',
            border: '3px solid #353a40', borderTop: '3px solid #f59e0b',
            borderRadius: '50%', animation: 'spin 1s linear infinite',
            margin: '0 auto 12px'
          }} />
          Chargement des volumes...
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '960px', margin: '0 auto' }}>
      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 9999,
          padding: '12px 20px', borderRadius: '10px',
          background: notification.type === 'success' ? 'rgba(74, 222, 128, 0.15)' : 'rgba(248, 113, 113, 0.15)',
          border: `1px solid ${notification.type === 'success' ? 'rgba(74, 222, 128, 0.4)' : 'rgba(248, 113, 113, 0.4)'}`,
          color: notification.type === 'success' ? '#4ade80' : '#f87171',
          fontSize: '0.875rem', fontWeight: 500,
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          {notification.type === 'success' ? '✓' : '✗'} {notification.message}
        </div>
      )}

      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
        <div>
          <h2 style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 600, margin: '0 0 4px 0' }}>
            Moniteur de Volumes
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
            Surveillance et reconnexion automatique des volumes réseau
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {lastRefresh && (
            <span style={{ color: '#8b919a', fontSize: '0.75rem' }}>
              Actualisé {lastRefresh.toLocaleTimeString('fr-FR')}
            </span>
          )}
          <button
            onClick={() => fetchStatus()}
            disabled={refreshing}
            style={{
              background: '#23262a', border: '1px solid #353a40',
              color: refreshing ? '#8b919a' : '#94a3b8',
              borderRadius: '8px', padding: '8px 14px',
              fontSize: '0.8rem', cursor: refreshing ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px',
              transition: 'all 0.2s'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }}>
              <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            Actualiser
          </button>
          <button
            onClick={handleRemountAll}
            disabled={actionLoading === 'remount-all'}
            style={{
              background: disconnectedCount > 0 ? '#f59e0b' : '#23262a',
              border: `1px solid ${disconnectedCount > 0 ? '#f59e0b' : '#353a40'}`,
              color: disconnectedCount > 0 ? '#181b1f' : '#94a3b8',
              borderRadius: '8px', padding: '8px 14px',
              fontSize: '0.8rem', fontWeight: 600,
              cursor: actionLoading === 'remount-all' ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {actionLoading === 'remount-all' ? '...' : '⟳ Tout remonter'}
          </button>
        </div>
      </div>

      {/* Résumé */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '28px' }}>
        {[
          { label: 'Total', value: volumes.length, color: '#94a3b8', bg: '#23262a' },
          { label: 'Connectés', value: mountedCount, color: '#4ade80', bg: 'rgba(74, 222, 128, 0.08)' },
          { label: 'Déconnectés', value: disconnectedCount, color: disconnectedCount > 0 ? '#f87171' : '#64748b', bg: disconnectedCount > 0 ? 'rgba(248, 113, 113, 0.08)' : '#23262a' },
        ].map(card => (
          <div key={card.label} style={{
            background: card.bg, border: `1px solid ${card.color}30`,
            borderRadius: '12px', padding: '16px 20px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{card.label}</span>
            <span style={{ color: card.color, fontSize: '1.75rem', fontWeight: 700 }}>{card.value}</span>
          </div>
        ))}
      </div>

      {/* Liste des volumes */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
        {volumes.map((vol) => (
          <div key={vol.mountPoint} style={{
            background: '#23262a',
            border: `1px solid ${vol.mounted ? '#353a40' : 'rgba(248, 113, 113, 0.4)'}`,
            borderRadius: '12px', padding: '18px 20px',
            transition: 'all 0.2s'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
              {/* Infos volume */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  {/* Indicateur statut */}
                  <div style={{
                    width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0,
                    background: vol.mounted ? '#4ade80' : '#f87171',
                    boxShadow: vol.mounted ? '0 0 6px rgba(74, 222, 128, 0.5)' : '0 0 6px rgba(248, 113, 113, 0.5)',
                    animation: !vol.mounted ? 'pulse 1.5s ease-in-out infinite' : 'none'
                  }} />
                  <span style={{ color: '#fff', fontWeight: 600, fontSize: '0.95rem', fontFamily: 'monospace' }}>
                    {vol.mountPoint}
                  </span>
                  <span style={{
                    padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 700,
                    textTransform: 'uppercase',
                    background: vol.mounted ? 'rgba(74, 222, 128, 0.15)' : 'rgba(248, 113, 113, 0.15)',
                    color: vol.mounted ? '#4ade80' : '#f87171',
                    border: `1px solid ${vol.mounted ? 'rgba(74, 222, 128, 0.3)' : 'rgba(248, 113, 113, 0.3)'}`
                  }}>
                    {vol.mounted ? 'Monté' : 'Déconnecté'}
                  </span>
                  <span style={{
                    padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem',
                    background: '#181b1f', color: '#64748b', border: '1px solid #353a40'
                  }}>
                    {vol.type.toUpperCase()}
                  </span>
                </div>

                <div style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: '8px', fontFamily: 'monospace' }}>
                  {vol.server}
                </div>

                <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '8px' }}>
                  {vol.description}
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {vol.services.length > 0 ? vol.services.map(svc => (
                    <span key={svc} style={{
                      padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem',
                      background: '#181b1f', color: '#a1a6ad',
                      border: '1px solid #353a40'
                    }}>
                      🐳 {svc}
                    </span>
                  )) : (
                    <span style={{ color: '#8b919a', fontSize: '0.75rem' }}>Aucun service Docker lié</span>
                  )}
                </div>
              </div>

              {/* Espace disque + actions */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px', flexShrink: 0 }}>
                {/* Espace disque */}
                {vol.mounted && vol.totalGB !== null && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginBottom: '4px' }}>
                      {vol.freeGB} Go libre / {vol.totalGB} Go
                    </div>
                    <div style={{ width: '120px', height: '4px', background: '#181b1f', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: '2px',
                        width: `${vol.usedPct ?? 0}%`,
                        background: (vol.usedPct ?? 0) > 85 ? '#f87171' : (vol.usedPct ?? 0) > 65 ? '#fbbf24' : '#4ade80',
                        transition: 'width 0.5s'
                      }} />
                    </div>
                    <div style={{ color: '#8b919a', fontSize: '0.7rem', marginTop: '2px', textAlign: 'right' }}>
                      {vol.usedPct}% utilisé
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  {!vol.mounted && (
                    <button
                      onClick={() => handleRemount(vol.mountPoint)}
                      disabled={actionLoading === `remount-${vol.mountPoint}`}
                      style={{
                        background: '#f59e0b', border: 'none',
                        color: '#181b1f', borderRadius: '7px',
                        padding: '7px 14px', fontSize: '0.78rem', fontWeight: 600,
                        cursor: actionLoading === `remount-${vol.mountPoint}` ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {actionLoading === `remount-${vol.mountPoint}` ? '...' : '⟳ Remonter'}
                    </button>
                  )}
                  {vol.mounted && (
                    <button
                      onClick={() => handleRemount(vol.mountPoint)}
                      disabled={!!actionLoading}
                      style={{
                        background: 'transparent', border: '1px solid #353a40',
                        color: '#94a3b8', borderRadius: '7px',
                        padding: '7px 14px', fontSize: '0.78rem',
                        cursor: actionLoading ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      ⟳ Remonter
                    </button>
                  )}
                  {vol.services.length > 0 && (
                    <button
                      onClick={() => handleRestartServices(vol.mountPoint)}
                      disabled={!!actionLoading}
                      style={{
                        background: 'transparent',
                        border: '1px solid #353a40',
                        color: vol.mounted ? '#e8eaed' : '#8b919a',
                        borderRadius: '7px', padding: '7px 14px', fontSize: '0.78rem',
                        cursor: (actionLoading || !vol.mounted) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s', opacity: vol.mounted ? 1 : 0.4
                      }}
                      title={!vol.mounted ? 'Montez le volume avant de redémarrer les services' : undefined}
                    >
                      {actionLoading === `restart-${vol.mountPoint}` ? '...' : '↺ Services'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Section logs */}
      <div style={{
        background: '#23262a', border: '1px solid #353a40',
        borderRadius: '12px', overflow: 'hidden'
      }}>
        <button
          onClick={() => { setShowLogs(v => !v); }}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 20px', background: 'transparent', border: 'none',
            color: '#94a3b8', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
            </svg>
            Journal du watchdog
          </span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            style={{ transition: 'transform 0.2s', transform: showLogs ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>

        {showLogs && (
          <div style={{
            borderTop: '1px solid #353a40',
            background: '#181b1f',
            maxHeight: '320px', overflowY: 'auto',
            fontFamily: 'monospace', fontSize: '0.78rem'
          }}>
            {logs.length === 0 ? (
              <div style={{ padding: '24px', color: '#8b919a', textAlign: 'center' }}>
                Aucun log disponible
              </div>
            ) : logs.map((entry, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '160px 52px 1fr',
                gap: '10px', padding: '5px 16px',
                background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
                borderLeft: `3px solid ${getLevelColor(entry.level)}20`
              }}>
                <span style={{ color: '#8b919a' }}>{entry.timestamp}</span>
                <span style={{
                  color: getLevelColor(entry.level),
                  background: getLevelBg(entry.level),
                  padding: '1px 6px', borderRadius: '4px',
                  fontSize: '0.7rem', fontWeight: 700, textAlign: 'center',
                  alignSelf: 'center'
                }}>
                  {entry.level}
                </span>
                <span style={{ color: '#cbd5e1', wordBreak: 'break-word' }}>{entry.message}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
};

export default VolumeMonitor;
